import { useLanguage } from "@/contexts/LanguageContext";
import { SERVICES, serviceInquiryHref } from "@shared/services";
import { ServiceDetails } from "@/components/ServiceOverview";
import { Button } from "@/components/ui/button";
import { LEGAL_NOTE, SITE } from "@/lib/site";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
type PageConfig = {
  title: string;
  eyebrow: string;
  intro: string;
  description: string;
  image: string;
  imageAlt: string;
  highlights: string[];
  sections: {
    title: string;
    body: string;
  }[];
  cta: string;
};
export const INFO_PAGES: Record<string, PageConfig> = {
  company: {
    title: "FIRMA IN NORDZYPERN GRÜNDEN",
    eyebrow: "FIRMENGRÜNDUNG NORDZYPERN",
    intro: "Von der ersten Einordnung bis zur Koordination vor Ort: Wir strukturieren den Prozess und halten die Beteiligten zusammen.",
    description: "Firmengründung in Nordzypern mit persönlicher Bedarfsanalyse, Dokumentencheck, Terminorganisation und Begleitung vor Ort.",
    image: SITE.images.meeting,
    imageAlt: "Persönliches Gespräch zur Firmengründung in Nordzypern",
    highlights: ["Setup-Check und Bedarfsanalyse", "Individuelle Dokumentencheckliste", "Koordination mit lokalen Ansprechpartnern", "Vorbereitung auf Banking und Termine"],
    sections: [{
      title: "ERST DAS VORHABEN. DANN DIE STRUKTUR.",
      body: "Eine Gesellschaftsform sollte zum tatsächlichen Vorhaben passen. Wir klären Tätigkeit, Gesellschafter, operative Ziele und den geplanten Aufenthalt, bevor einzelne Schritte koordiniert werden."
    }, {
      title: "VORBEREITUNG STATT PAPIERCHAOS.",
      body: "Du erhältst eine nachvollziehbare Checkliste. Wir stimmen Unterlagen, Übersetzungen, Beglaubigungen und Termine mit den jeweils geeigneten lokalen Partnern ab."
    }, {
      title: "TRANSPARENTE KOSTENARTEN.",
      body: "Unsere Service Fee wird von Fremdkosten getrennt. Behörden, Notar, Übersetzungen, Apostillen, Lizenzen, Banken oder Fachberater können zusätzliche Kosten verursachen."
    }],
    cta: "FIRMENGRÜNDUNG BESPRECHEN"
  },
  banking: {
    title: "KONTO IN NORDZYPERN ERÖFFNEN",
    eyebrow: "PRIVATKONTO · GESCHÄFTSKONTO",
    intro: "Vorbereiten, koordinieren, begleiten – damit du weißt, welche Unterlagen und Termine für eine Bankanfrage relevant sind.",
    description: "Unterstützung bei der Vorbereitung und Durchführung privater und geschäftlicher Kontoeröffnungen in Nordzypern.",
    image: SITE.images.meeting,
    imageAlt: "Vorbereitung eines Banktermins in Nordzypern",
    highlights: ["Prüfung der Ausgangslage", "Dokumentenvorbereitung", "Terminorganisation", "Persönliche Begleitung und Nachbereitung"],
    sections: [{
      title: "PRIVATKONTO",
      body: "Wir besprechen den Verwendungszweck, bereiten die verfügbaren Unterlagen vor und koordinieren – soweit möglich – einen passenden persönlichen Termin."
    }, {
      title: "GESCHÄFTSKONTO",
      body: "Bei Geschäftskonten betrachten wir Gesellschaft, Tätigkeit, wirtschaftlichen Hintergrund und die von der Bank angefragten Nachweise als zusammenhängenden Prozess."
    }, {
      title: "DIE BANK ENTSCHEIDET.",
      body: "CYPRUS PRIVÉ kann die Qualität der Vorbereitung beeinflussen, nicht die Entscheidung der Bank. Eine Kontoeröffnung wird ausdrücklich nicht garantiert."
    }],
    cta: "BANKING ANFRAGEN"
  },
  businessAccount: {
    title: "GESCHÄFTSKONTO IN NORDZYPERN",
    eyebrow: "BUSINESS BANKING",
    intro: "Ein Geschäftskonto beginnt nicht mit einem Formular, sondern mit einer nachvollziehbaren Gesellschafts- und Geschäftsdokumentation.",
    description: "Vorbereitung, Terminorganisation und persönliche Begleitung für Geschäftskonto-Anfragen in Nordzypern.",
    image: SITE.images.meeting,
    imageAlt: "Unterlagen für ein Geschäftskonto",
    highlights: ["Gesellschaftsunterlagen ordnen", "Geschäftstätigkeit nachvollziehbar darstellen", "Banktermin koordinieren", "Rückfragen strukturiert begleiten"],
    sections: [{
      title: "BANKING ALS TEIL DES SETUPS.",
      body: "Wir betrachten Gesellschaftsgründung und Banking nicht isoliert. Die Qualität der Informationen, die wirtschaftliche Plausibilität und vollständige Unterlagen sind zentrale Bestandteile einer guten Vorbereitung."
    }, {
      title: "KEINE GARANTIE – KLARE BEGLEITUNG.",
      body: "Die Annahme einer Geschäftsbeziehung liegt ausschließlich bei der jeweiligen Bank. Wir koordinieren den Prozess und unterstützen bei der sachgerechten Vorbereitung."
    }],
    cta: "GESCHÄFTSKONTO BESPRECHEN"
  },
  business: {
    title: "BUSINESS IN NORDZYPERN",
    eyebrow: "ORIENTIERUNG FÜR UNTERNEHMER",
    intro: "Menschen, Märkte und Abläufe lassen sich besser vor Ort beurteilen. Wir verbinden Vorbereitung mit echter Orientierung.",
    description: "Persönliche Business-Orientierung in Nordzypern für Unternehmer, Selbstständige und Investoren.",
    image: SITE.images.discovery,
    imageAlt: "Küstenlandschaft in Nordzypern",
    highlights: ["Vorhaben einordnen", "Lokale Ansprechpartner", "Termine vor Ort", "Langfristige persönliche Begleitung"],
    sections: [{
      title: "NICHT NUR EINE ADRESSE.",
      body: "Ein internationales Setup betrifft operative Abläufe, Banking, Aufenthalt und belastbare Ansprechpartner. Wir bringen diese Themen in eine nachvollziehbare Reihenfolge."
    }, {
      title: "ERST ERLEBEN. DANN ENTSCHEIDEN.",
      body: "Die Discovery Experience gibt qualifizierten Interessenten die Möglichkeit, Nordzypern selbst kennenzulernen und wichtige Fragen persönlich zu klären."
    }],
    cta: "BUSINESS SETUP STARTEN"
  },
  northCyprus: {
    title: "NORDZYPERN VERSTEHEN",
    eyebrow: "REGIONEN · LEBEN · BUSINESS",
    intro: "Zwischen Kyrenia, Esentepe, Tatlısu, İskele und Famagusta unterscheiden sich Rhythmus, Infrastruktur und Möglichkeiten deutlich.",
    description: "Nordzypern kennenlernen: Regionen, Leben, Business, Aufenthalt und ausgewählte Möglichkeiten persönlich einordnen.",
    image: SITE.images.discovery,
    imageAlt: "Mediterrane Küstenstraße in Nordzypern",
    highlights: ["Girne / Kyrenia", "Esentepe und Tatlısu", "İskele", "Famagusta"],
    sections: [{
      title: "DEIN EIGENER EINDRUCK ZÄHLT.",
      body: "Online-Recherche kann Orientierung geben, ersetzt aber weder Ortskenntnis noch ein persönliches Bild. Wir helfen dir, relevante Regionen und Themen gezielt kennenzulernen."
    }, {
      title: "MEHR ALS URLAUB.",
      body: "Infrastruktur, Wege, Alltag, Geschäftskontakte und Aufenthaltsplanung sollten gemeinsam betrachtet werden – besonders dann, wenn aus Interesse eine konkrete Entscheidung werden soll."
    }],
    cta: "NORDZYPERN ERLEBEN"
  },
  relocate: {
    title: "AUSWANDERN NACH NORDZYPERN",
    eyebrow: "RELOCATION-ERSTORIENTIERUNG",
    intro: "Ein möglicher Lebensmittelpunkt ist eine persönliche Entscheidung. Wir helfen, Fragen zu Alltag, Regionen, Kontakten und Setup zu strukturieren.",
    description: "Erstorientierung für Menschen, die über Auswandern, Leben oder einen zweiten Lebensmittelpunkt in Nordzypern nachdenken.",
    image: SITE.images.discovery,
    imageAlt: "Nordzypern als möglicher Lebensmittelpunkt",
    highlights: ["Regionen vergleichen", "Alltag und Infrastruktur", "Business und Banking", "Aufenthaltsfragen mit Fachpartnern"],
    sections: [{
      title: "KEINE PAUSCHALANTWORTEN.",
      body: "Lebensmodell, Familie, Tätigkeit und Zeithorizont verändern die Anforderungen. Unser Setup-Check schafft eine persönliche Ausgangsbasis."
    }, {
      title: "VOR ORT PRÜFEN.",
      body: "Wir empfehlen, Nordzypern bewusst kennenzulernen, bevor weitreichende Entscheidungen getroffen werden. Dafür wurde die Discovery Experience entwickelt."
    }],
    cta: "RELOCATION BESPRECHEN"
  },
  living: {
    title: "LEBEN IN NORDZYPERN",
    eyebrow: "ALLTAG · REGIONEN · ORIENTIERUNG",
    intro: "Nordzypern kann vieles sein: zeitweiser Rückzugsort, Lebensmittelpunkt oder Basis für ein unternehmerisches Vorhaben.",
    description: "Leben in Nordzypern: persönliche Orientierung zu Regionen, Alltag, Business, Banking und Aufenthalt.",
    image: SITE.images.stay,
    imageAlt: "Mediterrane Terrasse mit Meerblick",
    highlights: ["Regionale Unterschiede", "Wohnen und Aufenthalt", "Wege und Infrastruktur", "Persönliches Netzwerk"],
    sections: [{
      title: "LEBEN LÄSST SICH NICHT AUS EINER TABELLE LESEN.",
      body: "Wir zeigen keine pauschale Idealwelt. Stattdessen helfen wir dir, Prioritäten festzulegen und relevante Regionen selbst zu erleben."
    }, {
      title: "STAY ALS ERSTER SCHRITT.",
      body: "Eine ausgewählte Unterkunft kann der ruhige Ausgangspunkt sein, um Nordzypern ohne Entscheidungsdruck kennenzulernen."
    }],
    cta: "PERSÖNLICH ORIENTIEREN"
  },
  invest: {
    title: "IN NORDZYPERN INVESTIEREN",
    eyebrow: "PRIVATE INVESTMENT ACCESS",
    intro: "Ausgewählte Möglichkeiten statt öffentlicher Projektflut. Erst Ziel und Rahmen verstehen, dann Optionen einordnen.",
    description: "Diskreter Zugang zu ausgewählten Immobilien- und Investmentmöglichkeiten in Nordzypern nach persönlicher Qualifizierung.",
    image: SITE.images.privateAccess,
    imageAlt: "Diskrete hochwertige Architektur am Mittelmeer",
    highlights: ["Ziel und Zeithorizont", "Investmentrahmen", "Region und Nutzung", "Persönliche Qualifizierung"],
    sections: [{
      title: "NICHT JEDES INVESTMENT GEHÖRT INS INTERNET.",
      body: "Wir veröffentlichen keine permanente Projekt- oder Preisschlacht. Passende Möglichkeiten werden qualifizierten Interessenten in einem persönlichen Prozess vorgestellt."
    }, {
      title: "CHANCEN BRAUCHEN PRÜFUNG.",
      body: "Rendite, Verfügbarkeit und Wertentwicklung werden nicht garantiert. Rechtliche, steuerliche und regulierte Themen gehören in die Hände qualifizierter Fachpartner."
    }],
    cta: "PRIVATE ACCESS BEANTRAGEN"
  },
  realEstate: {
    title: "IMMOBILIEN IN NORDZYPERN",
    eyebrow: "AUSGEWÄHLT. NICHT BELIEBIG.",
    intro: "Immobilien sind bei CYPRUS PRIVÉ nicht der erste Verkaufsschritt, sondern eine mögliche nächste Entscheidung nach persönlicher Orientierung.",
    description: "Ausgewählte Immobilien in Nordzypern über einen persönlichen, qualifizierten Private-Access-Prozess kennenlernen.",
    image: SITE.images.privateAccess,
    imageAlt: "Zeitgenössische Residenz mit Mittelmeerblick",
    highlights: ["Eigennutzung oder Vermietung", "Region und Lage", "Budget und Zahlungsplan", "Persönliche Projektvorstellung"],
    sections: [{
      title: "KEIN ÖFFENTLICHER PROJEKTKATALOG.",
      body: "Wir möchten nicht möglichst viele Objekte zeigen. Wir möchten verstehen, welche Kriterien für dich tatsächlich relevant sind."
    }, {
      title: "ERST LAND UND LEUTE KENNENLERNEN.",
      body: "Eine Immobilie ist eine langfristige Entscheidung. Deshalb verbinden wir den Zugang zu Projekten mit persönlicher Beratung und eigener Erfahrung vor Ort."
    }],
    cta: "IMMOBILIENZUGANG ANFRAGEN"
  },
  holidayRental: {
    title: "FERIENWOHNUNG IN NORDZYPERN",
    eyebrow: "CYPRUS PRIVÉ STAY",
    intro: "Ausgewählte Apartments, Villen und Aufenthalte als Ausgangspunkt für deine Zeit in Nordzypern.",
    description: "Ausgewählte Ferienwohnungen und Villen in Nordzypern persönlich anfragen und die Insel kennenlernen.",
    image: SITE.images.stay,
    imageAlt: "Hochwertige Ferienvilla in Nordzypern",
    highlights: ["Persönlicher Anfrageprozess", "Ausgewählte Unterkünfte", "Regionale Orientierung", "Business-Cross-Selling ohne Verkaufsdruck"],
    sections: [{
      title: "STAY IN NORTH CYPRUS.",
      body: "Zum Launch arbeitet CYPRUS PRIVÉ STAY bewusst mit einem Anfrageprozess. So können Zeitraum, Personen, Region und besondere Anforderungen persönlich geklärt werden."
    }, {
      title: "DU DENKST ÜBER MEHR ALS URLAUB NACH?",
      body: "Wer Nordzypern als möglichen Lebens-, Business- oder Investmentstandort prüft, kann seinen Aufenthalt mit einem persönlichen Setup-Gespräch verbinden."
    }],
    cta: "UNTERKUNFT ANFRAGEN"
  },
  about: {
    title: "DEIN PERSÖNLICHER ZUGANG ZU NORDZYPERN.",
    eyebrow: "ÜBER CYPRUS PRIVÉ",
    intro: "Menschen vor Ort, klare Prozesse und ein Ansprechpartner, der Business, Banking, Aufenthalt und ausgewählte Möglichkeiten zusammenbringt.",
    description: "CYPRUS PRIVÉ ist ein persönlicher Premium-Service für Unternehmer, Investoren und Nordzypern-Interessierte.",
    image: SITE.images.meeting,
    imageAlt: "Persönliche Beratung bei CYPRUS PRIVÉ",
    highlights: ["Persönlich", "Diskret", "Transparent", "Langfristig"],
    sections: [{
      title: "AUS EINEM EINFACHEN GEDANKEN.",
      body: "Wer über ein Unternehmen, ein Konto, einen zweiten Lebensmittelpunkt oder ein Investment nachdenkt, braucht mehr als einzelne Anbieter. Er braucht klare Prozesse und verlässliche Ansprechpartner."
    }, {
      title: "PASSENDE MÖGLICHKEITEN STATT MAXIMALER AUSWAHL.",
      body: "Wir möchten nicht möglichst viele Projekte präsentieren. Wir möchten verstehen, was zu deinem Ziel, deinem Rahmen und deiner persönlichen Planung passt."
    }],
    cta: "CYPRUS PRIVÉ KENNENLERNEN"
  }
};
export function InfoPage({
  pageKey
}: {
  pageKey: keyof typeof INFO_PAGES;
}) {
  const {
    tx,
    tf,
    href: localeHref,
    dateLocale
  } = useLanguage();
  const serviceKey = ({
    company: "firmengruendung",
    banking: "banking",
    realEstate: "immobilien",
    relocate: "relocation"
  } as Record<string, string>)[pageKey];
  const service = SERVICES.find(item => item.id === serviceKey);
  const basePage = INFO_PAGES[pageKey];
  const page = service ? {
    ...basePage,
    eyebrow: "CYPRUS PRIVÉ / " + service.name.toUpperCase(),
    intro: service.description,
    description: service.summary,
    cta: service.cta.toUpperCase()
  } : basePage;
  useDocumentMeta(`${page.title} | CYPRUS PRIVÉ`, page.description);
  return <main>
    <section className="relative isolate overflow-hidden bg-[#101010] text-white">
      <img src={page.image} alt={tx(page.imageAlt)} className="absolute inset-0 h-full w-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
      <div className="container relative py-24 sm:py-32 lg:py-40"><div className="max-w-4xl"><p className="text-[10px] font-bold tracking-[.23em] text-[#C4A46B]">{tx(page.eyebrow)}</p><h1 className="mt-6 display-lg font-semibold">{tx(page.title)}</h1><p className="mt-7 max-w-2xl text-lg leading-8 text-white/72">{tx(page.intro)}</p><Button asChild size="lg" className="mt-9 rounded-none bg-[#C4A46B] px-7 text-xs font-bold tracking-[.12em] text-[#101010] hover:bg-[#d5bd90]"><Link href={service ? serviceInquiryHref(service.interest) : "/setup-check"}>{tx(page.cta)} <ArrowRight /></Link></Button></div></div>
    </section>
    {tx(service ? <ServiceDetails serviceId={service.id} standalone /> : <>
    <section className="border-b border-[#DED6C8] bg-[#FFFDF9]"><div className="container grid sm:grid-cols-2 lg:grid-cols-4">{tx(page.highlights.map((item, index) => <div key={item} className="flex min-h-28 items-center gap-4 border-b border-[#DED6C8] py-6 sm:nth-[odd]:border-r lg:border-b-0 lg:border-r lg:last:border-r-0"><span className="font-display text-3xl text-[#A98950]">{tx(String(index + 1).padStart(2, "0"))}</span><p className="text-sm font-semibold">{tx(item)}</p></div>))}</div></section>
    <section className="paper-grain py-24 lg:py-32"><div className="container"><div className="mx-auto max-w-5xl space-y-20">{tx(page.sections.map((section, index) => <article key={section.title} className="grid gap-6 lg:grid-cols-[.4fr_1fr]"><div><p className="eyebrow">{tx(String(index + 1).padStart(2, "0"))}</p><div className="section-rule mt-4" /></div><div><h2 className="display-md font-semibold">{tx(section.title)}</h2><p className="mt-6 max-w-3xl text-base leading-8 text-[#66615A]">{tx(section.body)}</p></div></article>))}</div></div></section>
    </>)}
    <section className="bg-[#EEE7DB] py-16"><div className="container flex flex-col gap-6 md:flex-row md:items-center md:justify-between"><div className="flex max-w-3xl gap-4"><ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-[#A98950]" /><p className="text-sm leading-7 text-[#66615A]">{tx(LEGAL_NOTE)}</p></div><Button asChild className="shrink-0 rounded-none bg-[#1B1B1B] px-6 text-white hover:bg-black"><Link href="/rechtliche-hinweise">{tx("RECHTLICHE HINWEISE")}</Link></Button></div></section>
  </main>;
}
